import React from "react";
import { Link } from "react-router-dom";
import { Card, Box, Typography, Avatar } from "@mui/material";
import FadeInImage from "../../../components/ui/FadeInImage";
import { Article } from "../services/mockData";
import { useTranslation } from "react-i18next";

interface CustomArticle extends Article {
  readingTime?: number;
}

interface ArticleCardProps {
  article: CustomArticle;
  disabledNavigation?: boolean;
}

const ArticleCard: React.FC<ArticleCardProps> = ({
  article,
  disabledNavigation = false,
}) => {
  const { t } = useTranslation();
  const linkProps = !disabledNavigation
    ? {
        component: Link,
        to: `/blog/${article.id}`,
      }
    : {};

  const extraerTextoDeJSON = (nodo: any): string => {
    if (!nodo) return "";
    if (typeof nodo === "string") return nodo;
    if (Array.isArray(nodo)) return nodo.map(extraerTextoDeJSON).join(" ");
    if (typeof nodo === "object") {
      if (nodo.text) return nodo.text;
      if (nodo.content) return extraerTextoDeJSON(nodo.content);
      return Object.values(nodo).map(extraerTextoDeJSON).join(" ");
    }
    return "";
  };

  const calcularTiempoLectura = (texto: string) => {
    if (!texto) return 1;
    const palabrasPorMinuto = 200;
    const cantidadPalabras = texto.trim().split(/\s+/).length;
    const minutos = Math.ceil(cantidadPalabras / palabrasPorMinuto);
    return minutos || 1;
  };

  const textoPlanoCuerpo = extraerTextoDeJSON(article.content);
  const tiempoFinal =
    article.readingTime ||
    calcularTiempoLectura(textoPlanoCuerpo || article.excerpt || "");

  return (
    <Box
      {...linkProps}
      sx={{ textDecoration: "none", display: "block", height: "100%" }}
    >
      <Card
        elevation={0}
        sx={{
          position: "relative",
          height: "420px",
          borderRadius: "15px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          backgroundColor: "#fff",
          border: "1px solid #e2e8 slate",
          transition: "transform 0.3s ease, box-shadow 0.3s ease",
          "&:hover": {
            transform: "translateY(-5px)",
            boxShadow: "0 10px 20px rgba(0, 0, 0, 0.05)",
          },
        }}
      >
        {article.image && (
          <FadeInImage
            src={article.image}
            alt={article.title}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              zIndex: 0,
            }}
          />
        )}

        <Box
          sx={{
            position: "absolute",
            top: "15px",
            left: "15px",
            backgroundColor: "#0069a7",
            color: "#ffffff",
            padding: "4px 10px",
            borderRadius: "4px",
            fontSize: "0.65rem",
            fontWeight: 700,
            zIndex: 2,
          }}
        >
          {/* Vuelve a ser 100% dinámico desde la BD */}
          {article.category}
        </Box>

        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            padding: "20px",
            backgroundColor: "rgba(0, 46, 77, 0.85)",
            color: "#fff",
            height: "200px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontSize: "1.2rem",
              fontWeight: 700,
              marginBottom: "8px",
              lineHeight: 1.3,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              overflowWrap: "break-word",
              wordBreak: "break-word",
            }}
          >
            {article.title}
          </Typography>

          <Typography
            sx={{
              color: "rgba(255, 255, 255, 0.8)",
              fontSize: "0.85rem",
              lineHeight: 1.5,
              flexGrow: 1,
              overflowY: "auto",
              overflowWrap: "break-word",
              wordBreak: "break-word",
            }}
          >
            {article.excerpt}
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderTop: "1px solid rgba(255, 255, 255, 0.2)",
              padding: "12px",
              marginTop: "auto",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Avatar
                sx={{
                  width: "22px",
                  height: "22px",
                  backgroundColor: "#fff",
                  color: "#0069a7",
                  fontSize: "0.6rem",
                  fontWeight: 700,
                }}
              >
                {article.authorAvatar || "CG"}
              </Avatar>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
                {t("blog.readTime", { time: tiempoFinal })}
              </Typography>
            </Box>
            <Typography
              sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#fff" }}
            >
              {t("blog.read")} &rarr;
            </Typography>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default ArticleCard;
